'use client';

import { useEffect, useCallback, useState } from 'react';
import { useChat } from '@/lib/hooks/useChat';
import toast from 'react-hot-toast';
import Icon from '@/components/ui/AppIcon';

interface AISummaryBarProps {
  /** A short label shown in the bar, e.g. "Logistics" */
  pageLabel: string;
  /** The data to summarise — pass a stable JSON-serialisable snapshot */
  data: unknown;
  /** System prompt that tells Gemini what to focus on */
  systemPrompt: string;
}

export default function AISummaryBar({ pageLabel, data, systemPrompt }: AISummaryBarProps) {
  const { response, isLoading, error, sendMessage } = useChat(
    'GEMINI',
    'gemini/gemini-2.5-flash',
    false
  );

  const [summary, setSummary] = useState<string>('');
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    if (error) toast.error(error.message);
  }, [error]);

  useEffect(() => {
    if (response && !isLoading) {
      setSummary(response);
      setHasLoaded(true);
    }
  }, [response, isLoading]);

  const runSummary = useCallback(() => {
    const dataStr = JSON.stringify(data, null, 2);
    sendMessage(
      [
        { role: 'system', content: systemPrompt },
        {
          role: 'user',
          content: `Here is the current live data for the ${pageLabel} page. Please provide a concise 1-2 sentence summary highlighting the most important operational insights:\n\n${dataStr}`,
        },
      ],
      { temperature: 0.3, max_tokens: 200 }
    );
  }, [data, pageLabel, systemPrompt, sendMessage]);

  // Auto-run on first mount once data is available
  useEffect(() => {
    if (!hasLoaded && data !== null && data !== undefined) {
      const hasData =
        (Array.isArray(data) && (data as unknown[]).length > 0) ||
        (typeof data === 'object' && Object.keys(data as object).length > 0);
      if (hasData) {
        runSummary();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <div
      className="flex items-start gap-3 px-4 py-3 border-b border-gray-100"
      style={{ background: 'linear-gradient(90deg, #EFF9FE 0%, #f0f4ff 100%)' }}
    >
      {/* Gemini badge */}
      <div
        className="flex items-center gap-1.5 flex-shrink-0 mt-0.5 px-2.5 py-1 rounded-full text-[11px] font-bold tracking-wide"
        style={{ background: '#E89A3C', color: '#fff' }}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12 2L9.09 9.09 2 12l7.09 2.91L12 22l2.91-7.09L22 12l-7.09-2.91L12 2z" />
        </svg>
        AI
      </div>

      {/* Summary text */}
      <div className="flex-1 min-w-0">
        {isLoading ? (
          <div className="flex items-center gap-2">
            <div className="flex gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-[#E89A3C] animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-1.5 h-1.5 rounded-full bg-[#E89A3C] animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-1.5 h-1.5 rounded-full bg-[#E89A3C] animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
            <span className="text-xs text-gray-400">Summarising {pageLabel} data…</span>
          </div>
        ) : summary ? (
          <p className="text-xs text-gray-700 leading-relaxed whitespace-normal break-words">
            {summary}
          </p>
        ) : (
          <p className="text-xs text-gray-400 italic">Click refresh to generate an AI summary.</p>
        )}
      </div>

      {/* Refresh button */}
      <button
        onClick={runSummary}
        disabled={isLoading}
        className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-80 active:scale-95"
        style={{ background: '#1C2E5E', color: '#fff' }}
        title="Refresh AI summary"
        aria-label="Refresh AI summary"
      >
        <Icon
          name="ArrowPathIcon"
          size={12}
          className={isLoading ? 'animate-spin' : ''}
        />
        <span className="hidden sm:inline">Refresh</span>
      </button>
    </div>
  );
}
